import { Component, OnInit, Input, Output, EventEmitter, ChangeDetectorRef } from '@angular/core';
import { IonicSharedModule } from '../../../ionic-shared.module';
import { AuthService } from '../../../services/auth.service';
import { DateUtilsService } from '../../../services/date-utils.service';
import { TranslateService } from '@ngx-translate/core';
import { Router } from '@angular/router';
import { FormBuilder, FormGroup, Validators } from "@angular/forms";
import { LoadingController, AlertController, NavController, ModalController, ToastController } from '@ionic/angular';
import { HttpClient } from '@angular/common/http'
import { UserService } from '../../../services/user.service';
import { Animal, AnimalActivity } from '../../../models/animal';
import { NetworkService } from '../../../services/network.service';
import { AnimalService } from '../../../services/animal.service';
import { EventsService } from '../../../services/events.service';
import { ActivityRecorderService, TrackPoint } from '../../../services/activity-recorder.service';
import { WeatherService } from '../../../services/weather.service';

import { DatetimeButtonComponent } from '../../../component/datetime-button/datetime-button.component';
import { AnimalSelectComponent } from '../../../component/animal-select/animal-select.component';
import GPX from 'ol/format/GPX';
import { getDistance } from 'ol/sphere';

@Component({
  selector: 'activity-edit',
  standalone: true,
  templateUrl: './activity-edit.component.html',
  styleUrls: ['./activity-edit.component.scss'],
  imports: [IonicSharedModule, DatetimeButtonComponent, AnimalSelectComponent]
})
export class ActivityEditComponent implements OnInit {

  @Output() submitDoneEvent = new EventEmitter<any>();

  _animal: Animal | null = null;
  @Input() set animal(value: Animal | null) {
    this._animal = value;
    if (this._activity == null) {
      if (this._animal) {
        this.form.controls['animals'].setValue([this._animal]);
      }
      this.date = this.dateUtils.toStringIso(new Date());
      this.form.controls['date'].setValue(this.date);
      this.form.controls['harness'].setValue(this.getLatestActivityValue('harness', ''));
      const defaultVis = this.getLatestActivityValue('visibility', '2');
      this.form.controls['visibility'].setValue(defaultVis);
    }
  }

  @Input() set allAnimals(value: Animal[]) {
    this._allAnimals = value;
    if (this._allAnimals.length === 1 && this._activity == null && !this.form.controls['animals'].value?.length) {
      this.form.controls['animals'].setValue([this._allAnimals[0]]);
    }
  }
  get allAnimals() { return this._allAnimals; }
  private _allAnimals: Animal[] = [];
  @Input() isFromLiveRecording = false;
  
  _recordedData: { distance: number, duration: number, gpxContent: string, elevationGain?: number } | null = null;
  get recordedData() { return this._recordedData; }
  @Input() set recordedData(value: { distance: number, duration: number, gpxContent: string, elevationGain?: number } | null) {
    console.log('ActivityEditComponent: received recordedData:', value);
    this._recordedData = value;
    if (value) {
      this.isFromLiveRecording = true;
      const mins = Math.floor(value.duration);
      const secs = Math.round((value.duration - mins) * 60);
      this.form.patchValue({
        distance: Number(value.distance.toFixed(2)),
        durationMinutes: mins,
        durationSeconds: secs,
        date: this.dateUtils.toStringIso(new Date()),
        type: 'ride',
        elevationGain: value.elevationGain || 0
      });
      this.entryMode = 'gpx';
      this.fetchWeather(true);
      this.cdr.detectChanges();
    }
  }

  _activity: AnimalActivity | null = null;
  @Input() set activity(value: AnimalActivity | null) {
    this._activity = value;
    if (this._activity) {
      this.date = this._activity.date ?? this.dateUtils.toStringIso(new Date());
      const totalMinutes = this._activity.duration || 0;
      const minutes = Math.floor(totalMinutes);
      const seconds = Math.round((totalMinutes % 1) * 60);

      this.entryMode = this._activity.gpxFile ? 'gpx' : 'manual';

      this.form.patchValue({
        date: this.date,
        type: this._activity.type,
        distance: this._activity.distance,
        durationMinutes: minutes,
        durationSeconds: seconds,
        difficulty: this._activity.difficulty,
        dogBehavior: this._activity.dogBehavior,
        harness: this._activity.harness,
        isTethered: this._activity.isTethered,
        temperature: this._activity.temperature,
        weather: this._activity.weather,
        visibility: this._activity.visibility,
        comment: this._activity.comment,
        humidity: this._activity.humidity,
        animalRating: this._activity.animalRating || 3,
        elevationGain: this._activity.elevationGain || 0,
        animals: this._activity.animals || (this._animal ? [this._animal] : [])
      });
    } else if (!this._recordedData) {
        this.form.reset({
          date: this.dateUtils.toStringIso(new Date()),
          type: 'other',
          difficulty: 3,
          isTethered: true,
          temperature: 15,
          weather: '["sun"]',
          visibility: this.getLatestActivityValue('visibility', '2'),
          harness: this.getLatestActivityValue('harness', ''),
          durationMinutes: 0,
          durationSeconds: 0,
          humidity: 50,
          animalRating: 3,
          elevationGain: 0,
          animals: this._animal ? [this._animal] : []
        });
       setTimeout(() => {
         this.fetchWeather(true); // silent = true for initial load
       }, 1000);
    }
  }

  @Input() showSaveButton = false;
  
  public form: FormGroup = new FormGroup({});
  public isSubmitted = false;
  weatherUserModified = false;
  loading: HTMLIonLoadingElement | null = null;
  date: string = '';
  entryMode: 'manual' | 'gpx' = 'manual';
  activityTypes = ['canicross', 'canivtt', 'agility', 'pistage', 'ring', 'ride', 'flatwork', 'jumping', 'lunging', 'competition', 'other'];

  ratings = [
    { value: 1, icon: '😞', color: '#ff4961' },
    { value: 2, icon: '😐', color: '#ffc409' },
    { value: 3, icon: '🙂', color: '#2dd36f' },
    { value: 4, icon: '😊', color: '#3880ff' },
    { value: 5, icon: '🤩', color: '#7044ff' }
  ];


  getFullSelectedAnimals(): Animal[] {
    const selectedAnimals: any[] = this.form.get('animals')?.value || [];
    return selectedAnimals.map(sa => {
      const fullAnimal = this.allAnimals.find(a => a.id === sa.id);
      return fullAnimal || sa;
    });
  }

  getFilteredActivityTypes(): string[] {
    const animals = this.getFullSelectedAnimals();
    const species = Array.from(new Set(animals.map(a => a.species?.name?.toLowerCase() || '')));
    
    const isDog = species.includes('chien') || species.includes('dog');
    const isHorse = species.includes('cheval') || species.includes('horse') || species.includes('poney') || species.includes('pony');
    
    let allowed: string[] = [];

    if (!isDog && !isHorse) {
      // If we don't know the species yet (or generic), start with all types
      allowed = [...this.activityTypes]; 
    } else {
      // Species-specific defaults
      allowed = ['other', 'ride', 'competition'];
      if (isDog) allowed = [...allowed, 'canicross', 'canivtt', 'agility', 'pistage', 'ring'];
      if (isHorse) allowed = [...allowed, 'flatwork', 'jumping', 'lunging'];
    }

    // Always filter for live recording stats support
    if (this.isFromLiveRecording) {
      allowed = allowed.filter(t => this.isTypeWithStats(t));
    }

    return this.activityTypes.filter(t => allowed.includes(t));
  }

  constructor(
    private http: HttpClient,
    public dateUtils: DateUtilsService,
    private authService: AuthService,
    private navCtrl: NavController,
    public loadingController: LoadingController,
    private translate: TranslateService,
    public fb: FormBuilder,
    public alertController: AlertController,
    public userService: UserService,
    public networkService: NetworkService,
    public animalService: AnimalService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private modalController: ModalController,
    private eventsService: EventsService,
    private recorder: ActivityRecorderService,
    private weatherService: WeatherService,
    private toastController: ToastController) {
    this.form = this.fb.group({
      date: ["", [Validators.required]],
      type: ["other", [Validators.required]],
      distance: [null],
      durationMinutes: [null],
      durationSeconds: [null],
      difficulty: [3],
      dogBehavior: [""],
      harness: [""],
      isTethered: [true],
      temperature: [20],
      weather: ["sun"],
      visibility: ["2"],
      humidity: [50],
      animalRating: [3],
      latitude: [null, [Validators.min(-90), Validators.max(90)]], // Just a hint for lat/lon
      longitude: [null, [Validators.min(-180), Validators.max(180)]],
      gpxFile: [null],
      elevationGain: [0],
      comment: [""],
      animals: [[], [Validators.required]]
    })

    // Auto-reset mode if type doesn't support GPX
    this.form.get('type')?.valueChanges.subscribe(() => {
      if (!this.isTypeWithStats()) {
        this.entryMode = 'manual';
      }
    });
  }

  isDogSelected(): boolean {
    const animals = this.getFullSelectedAnimals();
    const species = Array.from(new Set(animals.map(a => a.species?.name?.toLowerCase() || '')));
    return species.includes('chien') || species.includes('dog') || animals.length === 0;
  }

  isHorseSelected(): boolean {
    const animals = this.getFullSelectedAnimals();
    const species = Array.from(new Set(animals.map(a => a.species?.name?.toLowerCase() || '')));
    return species.includes('cheval') || species.includes('horse') || species.includes('poney') || species.includes('pony');
  }

  clearIfZero(controlName: string) {
    const control = this.form.get(controlName);
    if (control && (control.value === 0 || control.value === '0')) {
      control.setValue(null);
    }
  }

  setZeroIfEmpty(controlName: string) {
    const control = this.form.get(controlName);
    if (control && (control.value === null || control.value === '')) {
      control.setValue(0);
    }
  }

  ngOnInit() {}

  async presentLoading() {
    this.loading = await this.loadingController.create({
      message: this.translate.instant('Please wait...'),
    });
    await this.loading.present();
  }

  stopLoading() {
    this.loading?.dismiss();
    this.loading = null;
  }

  async submitForm() {
    this.isSubmitted = true;
    if (!this.form.valid) {
      const alert = await this.alertController.create({
        header: this.translate.instant("Warning"),
        message: this.translate.instant("One or more entries are in error, please correct them."),
        buttons: ['OK'],
      });
      await alert.present();
      return false;
    }
    await this.submitFormExecute();
    return true;
  }

  async submitFormExecute() {
    let values = { ...this.form.value };
    const primaryAnimal = this._animal || (values.animals && values.animals.length > 0 ? values.animals[0] : null);
    
    if (!primaryAnimal) {
       console.error('submitFormExecute: No animal selected');
       const alert = await this.alertController.create({
         header: this.translate.instant("Error"),
         message: this.translate.instant("Please select at least one animal."),
         buttons: ['OK'],
       });
       await alert.present();
       return;
    }

    let activity = this._activity ?? new AnimalActivity();
    
    // Ensure weather is stored as a JSON string if it's an array, or just leave it
    // The backend now expects TextField, we'll store it as a comma separated list or similar.
    // Let's use JSON string for consistency with user's hint.
    if (Array.isArray(values.weather)) {
      values.weather = JSON.stringify(values.weather);
    }

    // Convert minutes and seconds to total float minutes for the backend
    const mins = values.durationMinutes || 0;
    const secs = values.durationSeconds || 0;
    values.duration = mins + (secs / 60);
    
    // Clean up temporary form fields
    delete values.durationMinutes;
    delete values.durationSeconds;

    Object.assign(activity, values);

    // Filter out animals list for the payload, we'll send animalIds separately
    // The backend SaveAnimalActivity expects animalIds as a list of IDs.
    const animalIds = (values.animals || []).map((a: any) => a.id);
    const payload = { ...values, animalIds: animalIds };
    delete payload.animals;

    await this.presentLoading();
    // We can use any of the selected animals as the primary animal for the service call, 
    // but the backend SaveAnimalActivity now handles animalIds properly.
    
    if (this._recordedData) {
      (activity as any).gpxFile = {
        base64: 'data:application/gpx+xml;base64,' + btoa(unescape(encodeURIComponent(this._recordedData.gpxContent))),
        name: `recorded_${new Date().getTime()}.gpx`
      };
    } else if (values.gpxFile && values.gpxFile.base64) {
      (activity as any).gpxFile = values.gpxFile;
    }

    const hasGpx = !!this._recordedData || !!(values.gpxFile && values.gpxFile.base64);
    (activity as any).weatherPending =
      !this.networkService.isOnline() && !this.weatherUserModified && hasGpx;

    this.animalService.dbSaveAnimalActivity(primaryAnimal, activity).then(() => {
      this.isSubmitted = false;
      this.eventsService.publish('activity:saved', activity);
      this.submitDoneEvent.emit();
    }).finally(() => {
      this.stopLoading();
      if (this.showSaveButton) {
        this.modalController.dismiss(activity);
      }
    });
  }

  async deleteActivityCB() {
    if (!this._activity || !this._animal) return;

    const alert = await this.alertController.create({
      header: this.translate.instant('Delete activity'),
      message: this.translate.instant('Are you sure you want to delete this activity?'),
      buttons: [
        { text: this.translate.instant("No") },
        {
          text: this.translate.instant("Yes, delete it"),
          handler: async () => {
             await this.presentLoading();
             this.animalService.dbDeleteAnimalActivity(this._animal!, this._activity!).then(() => {
               this.submitDoneEvent.emit();
             }).finally(() => {
               this.stopLoading();
             });
          }
        }
      ]
    });
    await alert.present();
  }

  async onFileSelected(event: any) {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e: any) => {
        const content = e.target.result;
        const base64 = content;
        
        this.form.patchValue({
          gpxFile: {
            base64: base64,
            name: file.name
          }
        });

        // Extract data from GPX
        try {
          const gpxFormat = new GPX();
          // Extract the actual GPX XML string from base64 data URL
          const xmlString = atob(base64.split(',')[1]);
          const features = gpxFormat.readFeatures(xmlString);
          
          let totalDist = 0;
          let totalElevationGain = 0;
          let totalDurationMinutes = 0;
          let startDate: string | null = null;
          let allCoords: any[] = [];

          features.forEach(f => {
            const geom = f.getGeometry();
            if (geom) {
              const type = geom.getType();
              let coords: any[] = [];
              if (type === 'LineString') {
                coords = (geom as any).getCoordinates();
              } else if (type === 'MultiLineString') {
                (geom as any).getLineStrings().forEach((ls: any) => {
                  coords = coords.concat(ls.getCoordinates());
                });
              }

              if (coords.length >= 2) {
                allCoords = allCoords.concat(coords);
                // Distance & Elevation
                for (let i = 1; i < coords.length; i++) {
                  totalDist += getDistance([coords[i-1][0], coords[i-1][1]], [coords[i][0], coords[i][1]]);
                  
                  // Elevation (Z is index 2)
                  if (coords[i][2] !== undefined && coords[i-1][2] !== undefined) {
                    const diff = coords[i][2] - coords[i-1][2];
                    if (diff > 1.5) { // Threshold filter
                      totalElevationGain += diff;
                    }
                  }
                }
                
                // Start Date & Duration (M dimension holds timestamps in seconds - index 3)
                const startM = coords[0][3];
                const endM = coords[coords.length - 1][3];
                
                if (startM) {
                  // M is usually timestamp in seconds
                  const startD = new Date(startM * 1000);
                  startDate = this.dateUtils.toStringIso(startD);
                  
                  if (endM) {
                    totalDurationMinutes = (endM - startM) / 60;
                  }
                }
              }
            }
          });

          // Correct elevation gain with API for better accuracy
          const trackPoints: TrackPoint[] = allCoords.map((c: any) => ({ lat: c[1], lng: c[0], ele: c[2], time: '' }));
          try {
            const corrected = await this.recorder.correctElevationWithAPI(trackPoints);
            if (corrected.length > 0) {
              let correctedGain = 0;
              for (let i = 1; i < corrected.length; i++) {
                if (corrected[i].ele !== undefined && corrected[i - 1].ele !== undefined) {
                  const diff = (corrected[i].ele || 0) - (corrected[i - 1].ele || 0);
                  if (diff > 1.5) correctedGain += diff;
                }
              }
              if (correctedGain > 0) totalElevationGain = correctedGain;
            }
          } catch (apiErr) {
            console.warn('GPX Elevation correction API failed:', apiErr);
          }

          if (totalDist > 0) {
            const km = Number((totalDist / 1000).toFixed(2));
            this.form.patchValue({ distance: km });
          }

          if (totalElevationGain > 0) {
            this.form.patchValue({ elevationGain: Math.round(totalElevationGain) });
          }

          if (totalDurationMinutes > 0) {
            const mins = Math.floor(totalDurationMinutes);
            const secs = Math.round((totalDurationMinutes - mins) * 60);
            this.form.patchValue({
              durationMinutes: mins,
              durationSeconds: secs
            });
          }

          if (startDate) {
            this.date = startDate;
            this.form.patchValue({ date: startDate });
          }

          this.cdr.detectChanges();
        } catch (err) {
          console.error('Error parsing GPX:', err);
        }
      };
      reader.readAsDataURL(file);
    }
  }

  dtbDateActivityChange(event: any) {
    if (event.detail.value) {
      this.date = event.detail.value;
      this.form.controls['date'].setValue(this.date);
    }
  }

  formatDate(dateString: string): string {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString(this.translate.currentLang, { 
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit'
    });
  }

  getTypeIcon(type: string): string {
    switch(type) {
      case 'canicross': return 'custom_canicross.png';
      case 'canivtt': return 'custom_canivtt.png';
      case 'agility': return 'custom_agility.png';
      case 'pistage': return 'custom_pistage.png';
      case 'ring': return 'custom_ring.png';
      default: return 'walk.svg';
    }
  }

  setRating(value: number) {
    this.form.patchValue({ animalRating: value });
  }

  isTypeWithStats(typeOverride?: string): boolean {
    const type = typeOverride || this.form.get('type')?.value;
    return type === 'canicross' || type === 'canivtt' || type === 'ride' || type === 'competition' || type === 'pistage';
  }

  isTypeWithHarness(): boolean {
    const type = this.form.get('type')?.value;
    return type === 'canicross' || type === 'canivtt' || type === 'pistage';
  }

  toggleWeather(weather: string) {
    this.weatherUserModified = true;
    let current = this.form.value.weather;
    let weatherArr = [];
    try {
      weatherArr = JSON.parse(current);
      if (!Array.isArray(weatherArr)) weatherArr = [current];
    } catch(e) {
      weatherArr = current ? [current] : [];
    }

    if (weatherArr.includes(weather)) {
      weatherArr = weatherArr.filter(w => w !== weather);
    } else {
      weatherArr.push(weather);
    }
    
    this.form.controls['weather'].setValue(JSON.stringify(weatherArr));
  }

  isWeatherSelected(weather: string): boolean {
    let current = this.form.value.weather;
    try {
      let weatherArr = JSON.parse(current);
      return Array.isArray(weatherArr) ? weatherArr.includes(weather) : current === weather;
    } catch(e) {
      return current === weather;
    }
  }

  async fetchWeather(silent: boolean = false) {
    try {
      console.log('fetchWeather: Start', { silent, online: navigator.onLine });
      
      if (!navigator.geolocation) {
        console.error('fetchWeather: Geolocation not supported');
        if (!silent) await this.presentAlert(this.translate.instant('Error'), this.translate.instant("Geolocation is not supported by your browser"));
        return;
      }

      // Check navigator.onLine as well since networkService.isOnline() returns an Observable (which is always truthy)
      if (!navigator.onLine) {
        console.warn('fetchWeather: browser offline');
        if (!silent) await this.presentAlert(this.translate.instant('Error'), this.translate.instant("No internet connection (offline)"));
        return;
      }

      const options = {
        enableHighAccuracy: true,
        timeout: 20000, // Increase to 20s
        maximumAge: 0
      };

      const toast = await this.toastController.create({
        message: this.translate.instant('GPS_WEATHER_ACTIVE'),
        duration: 3000,
        position: 'top',
        color: 'primary',
        icon: 'partly-sunny-outline',
      });
      await toast.present();

      console.log('fetchWeather: Requesting position...');
      navigator.geolocation.getCurrentPosition(
        (position) => {
          console.log('fetchWeather: Position success', position.coords);
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          const url = `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current=temperature_2m,relative_humidity_2m,weather_code,is_day`;
          
          this.http.get(url).subscribe((res: any) => {
            console.log('fetchWeather: API success', res);
            if (res && res.current) {
              const temp = Math.round(res.current.temperature_2m);
              const hum = Math.round(res.current.relative_humidity_2m);
              const code = res.current.weather_code;
              const isDay = res.current.is_day;
              
              const weatherType = isDay === 0 ? 'night' : this.mapWeatherCode(code);
              
              this.form.patchValue({
                temperature: temp,
                humidity: hum,
                weather: JSON.stringify([weatherType]),
                latitude: lat,
                longitude: lon
              });
            }
          }, (error) => {
            console.error('fetchWeather: API error', error);
            if (!silent) this.presentAlert(this.translate.instant('Error'), "Failed to get weather data");
          });
        },
        (error) => {
          console.error('fetchWeather: Geolocation error', error);
          let msg = this.translate.instant("Could not get your location.");
          if (error.code === 1) msg = this.translate.instant("Location permission denied. Allow access in your device settings to enable automatic weather detection.");
          else if (error.code === 2) msg = this.translate.instant("Location unavailable. Make sure GPS is enabled on your device.");
          else if (error.code === 3) msg = this.translate.instant("Location request timed out. Try moving to an area with better GPS signal.");
          
          if (!silent) this.presentAlert(this.translate.instant('Error'), msg);
        },
        options
      );
    } catch (e) {
      console.error('fetchWeather: Critical Exception', e);
      if (!silent) await this.presentAlert("Critical Error", "An unexpected error occurred in fetchWeather");
    }
  }

  async presentAlert(header: string, message: string) {
    const alert = await this.alertController.create({
      header: header,
      message: message,
      buttons: ['OK']
    });
    await alert.present();
  }

  mapWeatherCode(code: number): string {
    return this.weatherService.mapWeatherCode(code);
  }

  getLatestActivityValue(field: string, defaultValue: string): string {
    if (this._animal && this._animal.activities && this._animal.activities.length > 0) {
      const sorted = [...this._animal.activities].sort((a: any, b: any) => b.date.localeCompare(a.date));
      return (sorted[0] as any)[field] ?? defaultValue;
    }
    return defaultValue;
  }

  dismiss() {
    this.modalController.dismiss();
  }

  formatDurationMinSec(decimalMinutes: number | undefined): string {
    if (decimalMinutes === undefined) return '0:00';
    const totalSeconds = Math.round(decimalMinutes * 60);
    const mins = Math.floor(totalSeconds / 60);
    const secs = totalSeconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }
}
